import type React from "react";
import { useState } from "react";
import { Box, Drawer, IconButton, useTheme } from "@mui/material";
import { HomeOutlined, MenuOutlined } from "@mui/icons-material";
import { TrendingUp, Assessment } from "@mui/icons-material";
import { SidebarMenuItem, StagingConfig } from "./sidebar/types/sidebar";
import { BaseURL } from "../service/Api";
import { StagingTag } from "./sidebar/StagingTag";
import { CollapseButton } from "./sidebar/CollapsibleButton";
import { SidebarMenu } from "./sidebar/SidebarMenu";
import { ProfileSection } from "./sidebar/ProfileButton";
import { SidebarContext } from "./sidebar/SidebarContext";
import type { IThemeState } from "../types/theme";
import { tokens } from "../theme";
import {
  LocationIcon,
  CrewIcon,
  DocumentIcon,
} from "../components/icons/index";
import {
  OperationsIcon,
  DashboardIcon,
  GearIcon,
  SettingsIcon,
  SalesIcon,
  AllCrewIcon,
  BookingsIcon,
  ContactIcon,
  ConfigurationIcon,
  MemoryIcon,
  DocumentLikeIcon,
  AcademyIcon,
  UserAdminIcon,
  EndpointRequestIcon,
} from "./sidebar/icons/index";
import { useSelector } from "react-redux";
import { RootState } from "../store/Index";
import Chema from "../assets/logo/Chema.png";
import BugReportIcon from "@mui/icons-material/BugReport";
import { AutologIcon } from "./sidebar/icons/AutologIcon";
import { PERMISSIONS, hasConfigurationAccess } from "../constants/permissions";
import { useNavigate } from "react-router-dom";

// import Chema from "../assets/logo/Chema.png";

const DRAWER_WIDTH = 280;
const COLLAPSED_WIDTH = 80;

const STAGING_CONFIG: StagingConfig = {
  isStaging: BaseURL === "https://staging.muzukuru.com",
  borderColor: "#f59e0b",
  glowColor: "rgba(245, 158, 11, 0.25)",
  badgeColor: "#fbbf24",
  badgeText: "STAGING",
};

interface SidebarProps {
  theme?: IThemeState;
  user?: {
    username?: string;
    first_name?: string;
    last_name?: string;
    profile_picture?: string | null;
  } | null;
  userPermissions?: string[];
}

const fallbackTheme: IThemeState = {
  id: 0,
  mode: "light",
  cities: [],
  primary_colors: {
    10: "#fdfaf7",
    50: "#f7efe9",
    100: "#ead7c8",
    200: "#d8bfa7",
    300: "#c6a684",
    400: "#614438",
    500: "#4f362c",
    600: "#3d2a22",
    700: "#2c1e18",
    800: "#1d1410",
    900: "#120c08",
  },
  secondary_colors: {
    10: "#fef8f1",
    50: "#fdeed8",
    100: "#f9dbb0",
    200: "#f2c686",
    300: "#e8b15b",
    400: "#d99a35",
    500: "#b67f2b",
    600: "#8c6121",
    700: "#614318",
    800: "#3a2a10",
    900: "#24170a",
  },
  neutral_colors: {
    10: "#ffffff",
    50: "#f9fafb",
    100: "#f3f4f6",
    200: "#e5e7eb",
    300: "#d1d5db",
    400: "#9ca3af",
    500: "#6b7280",
    600: "#4b5563",
    700: "#374151",
    800: "#1f2937",
    900: "#111827",
  },
  grey_colors: {
    10: "#f9fafb",
    50: "#f3f4f6",
    100: "#e5e7eb",
    200: "#d1d5db",
    300: "#9ca3af",
    400: "#6b7280",
    500: "#4b5563",
    600: "#374151",
    700: "#1f2937",
    800: "#111827",
    900: "#0b0f17",
  },
  logo: "/static/images/logo.png",
  logo_dark: "/static/images/logo.png",
  logo_light: "/static/images/logo.png",
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  company: 0,
  userModePreferences: {},
};

const Sidebar: React.FC<SidebarProps> = ({
  theme,
  user = null,
  userPermissions = [],
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({
    Sales: false,
    Operations: false,
    "Endpoint Request": false,
  });

  const muiTheme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(muiTheme.palette.mode, reduxTheme);
  const navigate = useNavigate();
  const authUser = useSelector((state: RootState) => state.auth.user);
  const resolvedTheme = theme ?? reduxTheme ?? fallbackTheme;
  const resolvedPermissions =
    userPermissions.length > 0 ? userPermissions : authUser?.permissions || [];

  const hasPermission = (permission?: string) => {
    if (!permission) return true;
    const trimmed = permission.trim().replace(/'$/, "");
    if (trimmed === PERMISSIONS.CONFIGURATION_VIEW) {
      return hasConfigurationAccess(resolvedPermissions);
    }
    return resolvedPermissions.includes(trimmed);
  };

  const mainMenuItems: SidebarMenuItem[] = [
    {
      title: "Dashboard",
      icon: <DashboardIcon />,
      path: "/dashboard",
      // permission: "authentication.view_customuser",
    },
    {
      title: "Finance",
      icon: <DocumentIcon />,
      children: [
        {
          title: "Cost Analysis",
          icon: <DocumentLikeIcon />,
          path: "/pending-approval",
        },
      ],
    },

    {
      title: "Consumer Sales",
      icon: <SalesIcon />,
      children: [
        {
          title: "Leads",
          icon: <DocumentLikeIcon />,
          path: "",
          permission: PERMISSIONS.QUOTE_REQUEST_VIEW,
        },
        {
          title: "Request for Quote",
          icon: <DocumentIcon />,
          path: "/request-for-quote",
          permission: PERMISSIONS.QUOTE_REQUEST_VIEW,
        },
        {
          title: "Referrals",
          icon: <DocumentIcon />,
          path: "/referrals",
          permission: PERMISSIONS.REFERRAL_VIEW,
        },

        {
          title: "Talk Show Sponsorships",
          icon: <DocumentIcon />,
          path: "/sponsorships",
          permission: PERMISSIONS.QUOTE_REQUEST_VIEW,
        },
        {
          title: "Stream Fund Applications",
          icon: <DocumentIcon />,
          path: "/streamfund",
          permission: PERMISSIONS.STREAM_FUND_VIEW,
        },
        {
          title: "Memorial Pages",
          icon: <DocumentIcon />,
          path: "/memorial-pages-dashboard",
          permission: PERMISSIONS.MEMORIAL_PAGES_VIEW,
        },
        {
          title: "Bookings",
          icon: <BookingsIcon />,
          permission: PERMISSIONS.BOOKING_VIEW,
          children: [
            {
              title: "Funeral",
              icon: <DocumentLikeIcon />,
              path: "/bookings/funeral",
            },

            {
              title: "Conference",
              icon: <DocumentLikeIcon />,
              path: "/bookings/conference",
            },
            {
              title: "Other",
              icon: <DocumentLikeIcon />,
              path: "/bookings/other",
            },
          ],
        },
        {
          title: "Contacts",
          icon: <ContactIcon />,
          path: "/contacts",
          permission: PERMISSIONS.CONTACT_VIEW,
        },
        {
          title: "Proposal",
          icon: <DocumentIcon />,
          path: "/proposals",
          permission: PERMISSIONS.PROPOSAL_VIEW,
        },
        {
          title: "Memory Table",
          icon: <MemoryIcon />,
          path: "/memory-table",
          permission: PERMISSIONS.COMMENT_VIEW,
        },
      ],
    },
    {
      title: "B2B Sales",
      icon: <SalesIcon />,
      children: [
        {
          title: "Leads",
          icon: <DocumentLikeIcon />,
          path: "/bookings/church",
          permission: PERMISSIONS.QUOTE_REQUEST_VIEW,
        },
        {
          title: "Funeral Homes ",
          icon: <ContactIcon />,
          path: "/bookings/funeral",
          permission: PERMISSIONS.BOOKING_VIEW,
        },
        {
          title: "Churches",
          icon: <ContactIcon />,
          path: "/bookings/church",
          permission: PERMISSIONS.BOOKING_VIEW,
        },
        {
          title: "Companies",
          icon: <ContactIcon />,
          path: "/companies",
          permission: PERMISSIONS.COMPANY_VIEW,
        },
        {
          title: "Insurance Companies",
          icon: <ContactIcon />,
          path: "/bookings/church",
          permission: PERMISSIONS.COMPANY_VIEW,
        },
        {
          title: "Listed Companies",
          icon: <ContactIcon />,
          path: "/bookings/church",
          permission: PERMISSIONS.COMPANY_VIEW,
        },
      ],
    },
    {
      title: "Operations",
      icon: <OperationsIcon />,
      children: [
        {
          title: "Streams",
          icon: <OperationsIcon />,
          path: "/stream-management",
          permission: PERMISSIONS.STREAM_VIEW,
        },
        // {
        //   title: "Streams",
        //   icon: <OperationsIcon />,
        //   permission: PERMISSIONS.STREAM_VIEW,
        //   children: [
        //     {
        //       title: "List Streams",
        //       icon: <OperationsIcon />,
        //       path: "/stream-management",
        //     },
        //     {
        //       title: "Callsheet",
        //       icon: <DocumentIcon />,
        //       path: "/bulk-crew-assignment",
        //       permission: PERMISSIONS.CALLSHEET_VIEW,
        //     },
        //   ],
        // },

        {
          title: "Venues",
          icon: <LocationIcon />,
          path: "/venues",
          permission: PERMISSIONS.VENUE_VIEW,
        },

        {
          title: "Crew Management",
          icon: <AllCrewIcon />,
          children: [
            {
              title: "Overview",
              icon: <Assessment />,
              path: "/crew-management",
              permission: PERMISSIONS.CREW_VIEW,
            },
            {
              title: "All Crew",
              icon: <CrewIcon />,
              path: "/crew-management/all-crew",
              permission: PERMISSIONS.CREW_VIEW,
            },
            {
              title: "Checklists",
              icon: <Assessment />,
              path: "/crew-management/checklists",
              permission: PERMISSIONS.CREW_VIEW,
            },
            {
              title: "Feedback & Ratings",
              icon: <Assessment />,
              path: "/crew-management/crew-ratings",
              permission: PERMISSIONS.CREW_VIEW,
            },
            {
              title: "Rates",
              icon: <TrendingUp />,
              path: "/crew-management/rates",
              permission: PERMISSIONS.CREW_VIEW,
            },
          ],
        },
        {
          title: "Gear Management",
          icon: <GearIcon />,
          path: "/gear-management",
          permission: PERMISSIONS.EQUIPMENT_VIEW,
        },
      ],
    },

    {
      title: "Academy",
      icon: <AcademyIcon />,
      permission: PERMISSIONS.ACADEMY_VIEW,
      children: [
        {
          title: "Overview",
          icon: <DashboardIcon />,
          path: "/courses/overview",
        },
        {
          title: "Courses",
          icon: <OperationsIcon />,
          path: "/courses",
        },
        {
          title: "Assessments",
          icon: <OperationsIcon />,
          path: "/assessments",
        },
        {
          title: "Badges ",
          icon: <AcademyIcon />,
          path: "/badges",
        },
      ],
    },
    {
      title: "Marketing",
      icon: <TrendingUp />,
      path: "/marketing",
    },
    {
      title: "Reports",
      icon: <Assessment />,
      path: "/reports",
    },
    {
      title: "Chema",
      icon: (
        <img
          src={Chema}
          alt="Chema"
          style={{ width: "24px", height: "24px", objectFit: "contain" }}
        />
      ),
      path: "/chema",
      permission: PERMISSIONS.DONATION_VIEW,
    },
    {
      title: "Settings",
      icon: <SettingsIcon />,
      path: "/settings",
      permission: PERMISSIONS.CONFIGURATION_VIEW,
    },
    {
      title: "Configurations",
      icon: <ConfigurationIcon />,
      path: "/configurations",
      permission: PERMISSIONS.CONFIGURATION_VIEW,
    },

    {
      title: "Autolog",
      icon: <AutologIcon />,
      path: "/autolog",
      permission: PERMISSIONS.CONFIGURATION_VIEW,
    },
    {
      title: "Bug Reports",
      icon: <BugReportIcon />,
      path: "/bug-reports",
      permission: PERMISSIONS.BUG_VIEW,
    },
    {
      title: "User Admin",
      icon: <UserAdminIcon />,
      path: "/user-management",
      permission: PERMISSIONS.USER_MANAGEMENT_DELETE,
    },
    {
      title: "Endpoint Request",
      icon: <EndpointRequestIcon />,
      children: [
        {
          title: "Admin",
          icon: <DashboardIcon />,
          path: "/endpoint-requests",
          permission: PERMISSIONS.ENDPOINT_REQUEST_ADMIN,
        },
        {
          title: "Frontend Developer",
          icon: <OperationsIcon />,
          path: "/endpoint-requests/frontend",
          permission: PERMISSIONS.ENDPOINT_REQUEST_FRONTEND_ADD_COMPONENT,
        },
        {
          title: "Backend Developer",
          icon: <OperationsIcon />,
          path: "/endpoint-requests/backend",
          permission: PERMISSIONS.ENDPOINT_REQUEST_BACKEND,
        },
      ],
    },
  ];

  const currentWidth = isCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;

  const sidebarContent = (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: colors.neutral[900],
        position: "relative",
        transition: "all 0.3s ease",
        ...(STAGING_CONFIG.isStaging && {
          borderLeft: `6px solid ${STAGING_CONFIG.borderColor}`,
          boxShadow: `inset 6px 0 20px ${STAGING_CONFIG.glowColor}`,
        }),
      }}
    >
      <StagingTag {...STAGING_CONFIG} />

      <Box sx={{ display: { xs: "none", md: "block" } }}>
        <CollapseButton theme={resolvedTheme} />
      </Box>

      <Box sx={{ p: isCollapsed ? 1 : 3, textAlign: "center", mb: 1 }}>
        {isCollapsed ? (
          <IconButton
            onClick={() => navigate("/app-home")}
            sx={{ color: colors.neutral[100] }}
          >
            <HomeOutlined fontSize="large" />
          </IconButton>
        ) : (
          <img
            src={
              reduxTheme?.mode === "dark"
                ? resolvedTheme.logo_dark ||
                  resolvedTheme.logo ||
                  "/static/images/logo.png"
                : resolvedTheme.logo_light ||
                  resolvedTheme.logo ||
                  "/static/images/logo.png"
            }
            alt="Logo"
            onClick={() => navigate("/app-home")}
            style={{
              width: "140px",
              cursor: "pointer",
              borderRadius: "5%",
            }}
          />
        )}
      </Box>

      <SidebarMenu
        items={mainMenuItems}
        theme={resolvedTheme}
        onPermissionCheck={hasPermission}
      />

      <ProfileSection user={user} theme={resolvedTheme} />
    </Box>
  );

  return (
    <SidebarContext.Provider
      value={{
        isCollapsed,
        toggleCollapse: () => setIsCollapsed(!isCollapsed),
        expandedMenus,
        toggleMenu: (key: string) => {
          setExpandedMenus((prev) => ({ ...prev, [key]: !prev[key] }));
        },
      }}
    >
      <Box className="sidebar" sx={{ display: "flex" }}>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={() => setMobileOpen(!mobileOpen)}
          sx={{
            position: "fixed",
            top: 16,
            left: 16,
            zIndex: 1200,
            display: { xs: "flex", md: "none" },
            backgroundColor: colors.neutral[900],
            boxShadow: 1,
            "&:hover": {
              backgroundColor: colors.neutral[800],
            },
          }}
        >
          <MenuOutlined />
        </IconButton>

        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: DRAWER_WIDTH,
              backgroundColor: colors.neutral[900],
            },
          }}
        >
          {sidebarContent}
        </Drawer>

        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            width: currentWidth,
            flexShrink: 0,
            transition: "width 0.3s ease",
            "& .MuiDrawer-paper": {
              width: currentWidth,
              boxSizing: "border-box",
              border: "none",
              backgroundColor: colors.neutral[900],
              transition: "width 0.3s ease",
              overflowX: "hidden",
            },
          }}
          open
        >
          {sidebarContent}
        </Drawer>

        <Box
          sx={{
            flex: 1,
            ml: 0,
            width: "100%",
          }}
        ></Box>
      </Box>
    </SidebarContext.Provider>
  );
};

export default Sidebar;
